import React from 'react'
import { Column } from 'primereact/column'

/**
 * Actions Column for custom action buttons
 * When adjacent to EditActionColumn, styled with borderLeft: none to visually merge
 * When standalone (no row editing), renders as normal column
 *
 * @param {Object} props
 * @param {Function} props.template - Custom actions template (rowData, options) => JSX
 * @param {boolean} props.mergeWithEdit - Whether this is adjacent to EditActionColumn
 * @param {string} props.dataKey - Primary key field name
 * @param {*} props.editingRowKey - Currently editing row's key value
 * @param {Object} props.editingRowKeyRef - React ref containing currently editing row's key value
 * @param {Object} props.editingRowKeysSetRef - React ref containing Set of all rows being edited
 * @param {Object} props.expandedRows - Current expanded rows state
 * @param {Function} props.onRowToggle - Handler to toggle row expansion
 * @returns {JSX.Element} Column component
 */
const ActionsColumn = ({ template, mergeWithEdit = false, dataKey, editingRowKey, editingRowKeyRef, editingRowKeysSetRef, expandedRows, onRowToggle }) => {
    const style = mergeWithEdit ? {
        textAlign: 'left',
        borderLeft: 'none',
        width: '120px',
        minWidth: '120px'
    } : {
        textAlign: 'left',
        width: '120px',
        minWidth: '120px'
    }

    // Wrapper to inject editing state into options
    const bodyTemplate = (rowData, options) => {
        if (typeof template === 'function') {
            // Check if this row is in the Set of editing rows
            // This supports multiple rows being edited simultaneously (e.g., newly added rows)
            const editingRowsSet = editingRowKeysSetRef?.current
            const isInEditingSet = editingRowsSet && editingRowsSet.has(rowData[dataKey])

            // Also check the single editing key as fallback
            const currentEditingKey = editingRowKeyRef?.current
            const isCurrentlyEditing = dataKey && currentEditingKey !== null && rowData[dataKey] === currentEditingKey

            // Row is editing if it's in the Set OR if it's the current editing row
            const isEditing = isInEditingSet || isCurrentlyEditing            
            const enhancedOptions = {
                ...options,
                isEditing,
                expandedRows,
                onRowToggle,
                dataKey
            }
            return template(rowData, enhancedOptions)
        }
        return null
    }

    return (
        <Column
            key={`actions-${editingRowKey ?? 'none'}`}
            header={mergeWithEdit ? '' : 'Actions'}
            body={bodyTemplate}
            style={style}
        />
    )
}

export default ActionsColumn
